import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import type {
  AdminEnvelope,
  AdminOrganizationAuditResponse,
  AdminOrganizationDetail,
  AdminOrganizationSearchResponse,
  AdminOrganizationSummary,
  AdminOrganizationsListParams,
  AdminOrganizationsListResponse,
  AdminOrgReviewQueueResponse,
  AdminOrgRole,
  AdminOrgRolesResponse,
} from '@/client/types/admin/users-orgs';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import useGetQueryFn from '../use-get-query-fn';

export const adminOrgKeys = {
  all: ['admin', 'organizations'] as const,
  list: (params: AdminOrganizationsListParams) =>
    [...adminOrgKeys.all, 'list', params] as const,
  summary: [...(['admin', 'organizations', 'summary'] as const)],
  detail: (oid: string | number | null) =>
    [...adminOrgKeys.all, 'detail', oid] as const,
  search: (term: string) => [...adminOrgKeys.all, 'search', term] as const,
  lookup: (params: Record<string, unknown>) =>
    [...adminOrgKeys.all, 'lookup', params] as const,
  listings: (oid: string | number | null, params: Record<string, unknown>) =>
    [...adminOrgKeys.all, 'listings', oid, params] as const,
  audit: (oid: string | number | null, params: Record<string, unknown>) =>
    [...adminOrgKeys.all, 'audit', oid, params] as const,
  orgAudit: (params: Record<string, unknown>) =>
    [...adminOrgKeys.all, 'org-audit', params] as const,
  reviewQueue: (params: Record<string, unknown>) =>
    [...adminOrgKeys.all, 'review-queue', params] as const,
  roleCatalog: (params: Record<string, unknown> = {}) =>
    [...adminOrgKeys.all, 'member-roles', params] as const,
  roleDetail: (ourid: number | null) =>
    [...adminOrgKeys.all, 'member-role', ourid] as const,
};

/** Accept either the bare paginated dict or the `{status, data}` envelope. */
const unwrapOrgsList = (raw: any): AdminOrganizationsListResponse => {
  const body =
    raw?.data &&
    (raw.data.organizations !== undefined ||
      raw.data.total_organizations !== undefined)
      ? raw.data
      : raw;
  return (
    body ?? {
      page: 1,
      per_page: 0,
      total_organizations: 0,
      total_pages: 0,
      organizations: [],
    }
  );
};

const unwrapOrgDetail = (raw: any): AdminOrganizationDetail | null => {
  const body = raw?.data?.oid !== undefined ? raw.data : raw?.oid !== undefined ? raw : raw?.data;
  return body?.oid != null ? (body as AdminOrganizationDetail) : null;
};

export const useAdminOrganizationsQuery = (
  params: AdminOrganizationsListParams = {}
) => {
  const queryFn = useGetQueryFn<unknown>({
    defaultErrorMessage:
      'An unexpected error occurred while fetching organizations',
    endpoint: '/api/admin/organizations',
    paginated: true,
  });

  return useQuery({
    queryKey: adminOrgKeys.list(params),
    queryFn: async () => unwrapOrgsList(await queryFn({ ...params })),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useAdminOrganizationSummaryQuery = (enabled = true) => {
  const queryFn = useGetQueryFn<AdminEnvelope<AdminOrganizationSummary>>({
    defaultErrorMessage:
      'An unexpected error occurred while fetching organization summary',
    endpoint: '/api/admin/organizations/summary',
  });

  return useQuery({
    queryKey: adminOrgKeys.summary,
    queryFn: () => queryFn({}),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

export const useAdminOrganizationDetailQuery = (
  oid: string | number | null,
  options: { enabled?: boolean } = {}
) => {
  const { enabled = true } = options;
  const hasOid = oid !== null && oid !== '' && oid !== undefined;

  const queryFn = useGetQueryFn<unknown>({
    defaultErrorMessage:
      'An unexpected error occurred while fetching the organization',
    endpoint: `/api/admin/organizations/${oid}`,
  });

  const query = useQuery({
    queryKey: adminOrgKeys.detail(oid),
    queryFn: async () => unwrapOrgDetail(await queryFn({})),
    enabled: hasOid && enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    retry: (failureCount, error) =>
      (error as any)?.status !== 404 && failureCount < 1,
  });

  const notFound = query.isError && (query.error as any)?.status === 404;

  return {
    ...query,
    data: (query.data ?? null) as AdminOrganizationDetail | null,
    notFound,
    isFetchError: query.isError && !notFound,
  };
};

export const useAdminOrganizationSearchQuery = (
  query: string | null,
  enabled = true,
  limit = 20
) => {
  const search = query?.trim() ?? '';

  const queryFn = useGetQueryFn<AdminEnvelope<AdminOrganizationSearchResponse>>({
    defaultErrorMessage:
      'An unexpected error occurred while searching organizations',
    endpoint: '/api/admin/organizations/search',
  });

  return useQuery({
    queryKey: adminOrgKeys.search(search),
    queryFn: () => queryFn({ search, limit }),
    enabled: Boolean(search) && enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data?.organizations ?? [],
  });
};

export const useAdminOrganizationLookupQuery = (
  params: {
    oid?: string | number;
    uuid?: string;
    ffid?: string;
    name?: string;
    owner_email?: string;
    active_only?: 0 | 1;
  } | null,
  enabled = true
) => {
  const queryFn = useGetQueryFn<AdminEnvelope<AdminOrganizationDetail>>({
    defaultErrorMessage:
      'An unexpected error occurred while looking up the organization',
    endpoint: '/api/admin/organizations/lookup',
  });

  const hasIdent = Boolean(
    params &&
      (params.oid != null ||
        params.uuid ||
        params.ffid ||
        params.name ||
        params.owner_email)
  );

  return useQuery({
    queryKey: adminOrgKeys.lookup(params ?? {}),
    queryFn: () => queryFn({ ...(params ?? {}) }),
    enabled: hasIdent && enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data ?? null,
    retry: (failureCount, error) =>
      (error as any)?.status !== 404 && failureCount < 1,
  });
};

export type AdminOrganizationListingsResult = {
  entities: unknown[];
  page: number;
  page_size: number;
  total_entities: number;
  total_pages: number;
};

export const useAdminOrganizationListingsQuery = (
  oid: string | number | null,
  params: { page?: number; page_size?: number } = {},
  enabled = true
) => {
  const queryFn = useGetQueryFn<
    AdminEnvelope<
      unknown[] | { entities?: unknown[]; results?: unknown[]; total?: number; total_entities?: number; page?: number; per_page?: number; page_size?: number; total_pages?: number }
    >
  >({
    defaultErrorMessage:
      'An unexpected error occurred while fetching organization listings',
    endpoint: `/api/admin/organizations/${oid}/listings`,
  });

  return useQuery({
    queryKey: adminOrgKeys.listings(oid, params),
    queryFn: () => queryFn({ ...params }),
    enabled: Boolean(oid) && enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    select: (response): AdminOrganizationListingsResult => {
      const data = response?.data as any;
      const page = params.page ?? 1;
      const pageSize = params.page_size ?? 12;

      if (Array.isArray(data)) {
        return {
          entities: data,
          page,
          page_size: pageSize,
          total_entities: data.length,
          total_pages: Math.max(1, Math.ceil(data.length / pageSize) || 1),
        };
      }

      const entities: unknown[] = Array.isArray(data?.entities)
        ? data.entities
        : Array.isArray(data?.results)
          ? data.results
          : [];
      const total =
        data?.total_entities ?? data?.total ?? entities.length;
      const perPage = data?.per_page ?? data?.page_size ?? pageSize;
      const totalPages =
        data?.total_pages ?? Math.max(1, Math.ceil(Number(total) / perPage) || 1);

      return {
        entities,
        page: data?.page ?? page,
        page_size: perPage,
        total_entities: Number(total) || 0,
        total_pages: totalPages,
      };
    },
  });
};

export const useAdminOrganizationAuditQuery = (
  oid: string | number | null,
  params: { limit?: number; skip?: number } = {},
  enabled = true
) => {
  const queryFn = useGetQueryFn<AdminEnvelope<AdminOrganizationAuditResponse>>({
    defaultErrorMessage:
      'An unexpected error occurred while fetching organization audit',
    endpoint: `/api/admin/organizations/${oid}/audit`,
  });

  return useQuery({
    queryKey: adminOrgKeys.audit(oid, params),
    queryFn: () => queryFn({ limit: 100, skip: 0, ...params }),
    enabled: Boolean(oid) && enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    select: (response) => response?.data,
  });
};

export const useAdminOrganizationsAuditQuery = (
  params: {
    target_type?: string;
    target_id?: string | number;
    actor_authid?: string;
    action?: string;
    outcome?: string;
    limit?: number;
    skip?: number;
  } = {}
) => {
  const queryFn = useGetQueryFn<AdminEnvelope<AdminOrganizationAuditResponse>>({
    defaultErrorMessage:
      'An unexpected error occurred while fetching the organizations audit log',
    endpoint: '/api/admin/organizations/audit',
  });

  return useQuery({
    queryKey: adminOrgKeys.orgAudit(params),
    queryFn: () => queryFn({ limit: 100, skip: 0, ...params }),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    select: (response) => response?.data,
  });
};

export const useOrgReviewQueueQuery = (
  params: { page?: number; page_size?: number } = {}
) => {
  const queryFn = useGetQueryFn<AdminEnvelope<AdminOrgReviewQueueResponse>>({
    defaultErrorMessage:
      'An unexpected error occurred while fetching the review queue',
    endpoint: '/api/admin/organizations/review-queue',
  });

  return useQuery({
    queryKey: adminOrgKeys.reviewQueue(params),
    queryFn: () => queryFn({ ...params }),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    select: (response) => response?.data,
  });
};

/**
 * Global org-role catalogue. Pass `permissions: 0` for the slim picker shape
 * (Members / invites). Default includes grants for the Roles console.
 */
export const useOrgRoleCatalogQuery = (
  params: { permissions?: 0 | 1 } = {},
  enabled = true
) => {
  const queryFn = useGetQueryFn<AdminEnvelope<AdminOrgRolesResponse>>({
    defaultErrorMessage:
      'An unexpected error occurred while fetching organization roles',
    endpoint: '/api/admin/organizations/member-roles',
  });

  return useQuery({
    queryKey: adminOrgKeys.roleCatalog(params),
    queryFn: () => queryFn({ ...params }),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data?.roles ?? [],
  });
};

export const useOrgRoleDetailQuery = (
  ourid: number | null,
  enabled = true
) => {
  const queryFn = useGetQueryFn<AdminEnvelope<AdminOrgRole>>({
    defaultErrorMessage:
      'An unexpected error occurred while fetching the organization role',
    endpoint: `/api/admin/organizations/member-roles/${ourid}`,
  });

  return useQuery({
    queryKey: adminOrgKeys.roleDetail(ourid),
    queryFn: () => queryFn({}),
    enabled: ourid != null && enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data ?? null,
  });
};
